<script>
'use strict';
const SearchHeader = require('./components/SearchHeader.vue');
const SearchTabs = require('./components/SearchTabs.vue');
const ProductsSection = require('./components/ProductsSection.vue');
const StoriesSection = require('./components/StoriesSection.vue');
const TopResultsSection = require('./components/TopResultsSection.vue');
const NoResultsSection = require('./components/NoResultsSection.vue');
const ErrorDialog = require('./components/ErrorDialog.vue');
const Survey = require('./components/Survey.vue');
const _debounce = require('lodash/debounce');
const { parseFiltersFromUrl } = require('../lib/url-filters');

module.exports = {
  name: 'app',
  components: {
    SearchHeader,
    SearchTabs,
    ProductsSection,
    StoriesSection,
    TopResultsSection,
    NoResultsSection,
    ErrorDialog,
    Survey,
  },
  data() {
    return { initialLoadDone: false };
  },
  created() {
    this.debouncedUpdateStickyTops = _debounce(this.updateMobileStickyTops, 50);

    const urlSearchParams = new URLSearchParams(window.location.search);
    const q = urlSearchParams.get('q');
    const initialFilters = parseFiltersFromUrl(urlSearchParams);

    this.$store.dispatch('loadSavedProducts');
    this.$store.dispatch('autoOptInUserOnPostAuth');

    const search = q
      ? this.$store.dispatch('submitSearch', { term: q, initialFilters })
      : this.$store.dispatch('showNoResults');

    search.finally(() => {
      this.initialLoadDone = true;
      this.$nextTick(() => {
        const el = document.querySelector('[data-dummy-loading]');

        if (!el) return;
        el.classList.add('is-hiding');
        el.addEventListener('transitionend', () => el.remove(), { once: true });
      });
    });
  },
  computed: {
    activeTab() {
      return this.$store.state.activeTab;
    },
    noResults() {
      return this.$store.state.noResults;
    },
  },
  mounted() {
    // Watch the zero-height sentinel inside SearchHeader (right below editorial-note-top).
    // When it exits the viewport from the top, the editorial note has scrolled away
    // and .search-wrapper has reached its sticky position. Start observing only after
    // initialLoadDone so the hidden app-container doesn't trigger a false positive.
    this.stickyObserver = new IntersectionObserver(
      ([entry]) => {
        if (window.innerWidth < 768) {
          this.$nextTick(() => this.$store.commit('setStickyOnMobile', !entry.isIntersecting));
        }
      },
      { threshold: 0 }
    );

    window.addEventListener('resize', this.debouncedUpdateStickyTops);
  },
  beforeDestroy() {
    this.stickyObserver.disconnect();
    window.removeEventListener('resize', this.debouncedUpdateStickyTops);
    this.debouncedUpdateStickyTops.cancel();
  },
  watch: {
    // Start the sentinel observer and measure heights once the app is visible.
    initialLoadDone(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.updateMobileStickyTops();
          this.stickyObserver.observe(this.$refs.searchHeader.$refs.stickysentinel);
        });
      }
    },
    // Recompute heights when results appear (tabs had offsetHeight 0 while hidden).
    noResults(newVal) {
      if (!newVal) this.$nextTick(() => this.updateMobileStickyTops());
    },
  },
  methods: {
    /**
     * Measures the sticky header stack heights and writes them as CSS custom
     * properties on the app container so each sticky element knows its top offset.
     * On >= 768px it resets isStickyOnMobile and exits early — the sticky stack
     * is inactive at tablet/desktop via the max-width media query.
     * Also re-syncs isStickyOnMobile from the sentinel's current position after a
     * resize, because IntersectionObserver won't re-fire on viewport size changes.
     */
    updateMobileStickyTops() {
      if (window.innerWidth >= 768) {
        this.$store.commit('setStickyOnMobile', false);
        return;
      }

      // Re-sync sticky state after resize (observer only fires on intersection change).
      const sentinel = this.$refs.searchHeader && this.$refs.searchHeader.$refs.stickysentinel;

      if (sentinel) {
        const top = sentinel.getBoundingClientRect().top;
        this.$store.commit('setStickyOnMobile', top < 0);
      }

      if (this.noResults) return;

      // $el is .search-header-container; measure only the sticky .search-wrapper
      // so --mobile-tabs-sticky-top reflects the pinned header height, not the full
      // container (which includes the non-sticky editorial-note-top above it).
      const searchWrapper = this.$refs.searchHeader.$el.querySelector('.search-wrapper');
      const tabs = this.$refs.searchTabs.$el;

      if (!searchWrapper || !tabs || !tabs.offsetHeight) return;

      const tabsStyle = window.getComputedStyle(tabs);
      const searchWrapperH = searchWrapper.offsetHeight;
      const tabsPaddingBottom = parseFloat(tabsStyle.paddingBottom) || 0;
      const tabsH = tabs.offsetHeight;
      const filtersEl = this.$el.querySelector('.product-filters');
      const filtersPaddingTop = filtersEl
        ? parseFloat(window.getComputedStyle(filtersEl).paddingTop) || 0
        : 20;

      // Sticky tabs: CSS constrains their padding-top edge to searchWrapperH from viewport.
      // Filters should appear immediately below tabs (including tabs' padding-bottom).
      let filtersStickyTop =
        searchWrapperH +
        tabsH +
        tabsPaddingBottom -
        filtersPaddingTop -
        19; // the gap between tabs and filters is reduced by 19px when sticky state is on

      // when some small screens changes from vertical to horizontal, recalculation is not right.
      if (filtersStickyTop < 103) {
        filtersStickyTop = 103;
      }

      this.$el.style.setProperty(
        '--mobile-tabs-sticky-top',
        `${searchWrapperH}px`
      );
      this.$el.style.setProperty(
        '--mobile-filters-sticky-top',
        `${filtersStickyTop}px`
      );
    },
  },
};
</script>

<template>
  <transition name="app-fade">
    <div class="app-container" v-show="initialLoadDone">
      <!-- Sentinel ref lives inside SearchHeader, right below editorial-note-top,
           so sticky fires when the note exits the viewport. -->
      <SearchHeader ref="searchHeader" />
      <NoResultsSection />
      <div v-show="!noResults">
        <SearchTabs ref="searchTabs" />
        <TopResultsSection v-show="activeTab === 'top-results'" />
        <ProductsSection v-show="activeTab === 'products'" />
        <StoriesSection v-show="activeTab === 'stories'" />
        <ErrorDialog />
        <Survey />
      </div>
    </div>
  </transition>
</template>

<style scoped lang="postcss"></style>
